// conventions-allow: file-size - MfrrBidTable component is large due to complex table rendering logic
import type { ReactNode } from 'react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { colors } from 'styles/variables'
import { getCellDisplay } from 'ui/components/HourlyDataTable/HourlyDataTable.cellEdit.helper'
import { useTableCellClipboard } from 'ui/components/HourlyDataTable/hooks/useTableCellClipboard'
import { useTableCellEdit } from 'ui/components/HourlyDataTable/hooks/useTableCellEdit'
import { useTableCellNavigation } from 'ui/components/HourlyDataTable/hooks/useTableCellNavigation'
import { useTableCellSelection } from 'ui/components/HourlyDataTable/hooks/useTableCellSelection'
import Datetime from 'utils/datetime/datetime'
import {
  hasCMMarketClosedForDeliveryDate,
  hasEAMMarketClosedForDeliveryRow,
} from 'views/MFRRTradingView/components/MFRRTradingDeadlineCounter/MFRRTradingDeadlineCounter.helper'
import { makeMfrrBidTableAdapter } from './MfrrBidTable.adapter'
import {
  CM_ROW_COUNT,
  CMBID_DIRECTIONS,
  EAM_QUARTER_MINUTES,
  EAM_ROW_COUNT,
  EMPTY_MFRR_BIDS,
  formatCmReadOnlyValue,
  getCmCellKey,
  getDirectionTotals,
  getEnabledMfrrProductionUnitKey,
  getMfrrCellEditRule,
  getMfrrDirectionBackground,
  getMfrrDirectionInnerBorder,
  getMfrrRowLabel,
  getMfrrRowStartTime,
  isValidMfrrBidValue,
  MFRR_TABLE_CELL_BORDER,
  MFRR_TABLE_CLOSED_ROW_BORDER,
  MFRR_TABLE_GROUP_BORDER,
  mapMfrrBidsToRowValues,
  mapPublishedCmBidVolumesToEamRowValues,
  mapRowValuesToMfrrBids,
  parseCmCellKey,
} from './MfrrBidTable.helper'
import type { CmDirection, CmField, MfrrBidTableProps, MfrrBidTableRowProps } from './MfrrBidTable.types'
import { getMarketResultHourForRow, getMfrrValueFieldsPerDirection } from './MfrrMarketResults.helper'

function createInitialRows(rowCount: number, initialValues?: Record<string, string>[]): Record<string, string>[] {
  return Array.from({ length: rowCount }, (_unused, hourIndex) => ({
    ...(initialValues?.[hourIndex] ?? {}),
  }))
}

const MfrrBidTableRow = memo(function MfrrBidTableRow({
  hourIndex,
  market,
  isRowDisabled,
  isLatestDisabledRow,
  isMarketResultViolationRow,
  units,
  rowValues,
  activeCellKey,
  selectedCellKeys,
  marketResultUnitKeys,
  getMarketResultVolume,
  onValueChange,
  onCellFocus,
  onCellBlur,
  onCellKeyDown,
  onCellMouseDown,
  onCellMouseOver,
  registerInputRef,
}: MfrrBidTableRowProps): ReactNode {
  const { t } = useTranslation()

  function renderEditableCell(
    direction: CmDirection,
    unit: MfrrBidTableRowProps['units'][number],
    field: CmField,
    borderRight: string
  ): ReactNode {
    const unitKey = getEnabledMfrrProductionUnitKey(unit)
    const cellKey = getCmCellKey(hourIndex, direction, unitKey, field)
    const rawValue = rowValues[cellKey] ?? ''
    const cellDisplay = getCellDisplay(rawValue, getMfrrCellEditRule(market, field))
    const displayValue = cellDisplay === '' && activeCellKey !== cellKey ? '0' : cellDisplay
    const isValid = isValidMfrrBidValue(rawValue, field, market)

    return (
      <td
        key={cellKey}
        className={[
          'p-0',
          !isValid
            ? 'bg-red-50'
            : isMarketResultViolationRow
              ? 'bg-yellow-100'
              : isRowDisabled
                ? 'bg-gray-100'
                : 'bg-white',
          selectedCellKeys.has(cellKey) ? 'ring-2 ring-blue-400 ring-inset' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{
          borderRight,
          borderBottom: isLatestDisabledRow ? MFRR_TABLE_CLOSED_ROW_BORDER : MFRR_TABLE_CELL_BORDER,
        }}
        data-cell-key={cellKey}
        onMouseDown={isRowDisabled ? undefined : (ev) => onCellMouseDown(cellKey, ev)}
        onMouseOver={isRowDisabled ? undefined : () => onCellMouseOver(cellKey)}
        onFocus={isRowDisabled ? undefined : () => onCellMouseOver(cellKey)}
      >
        <input
          type="text"
          inputMode="decimal"
          ref={(element) => registerInputRef(cellKey, element)}
          aria-label={t('{{direction}} {{unit}} {{field}} for hour {{hour}}', {
            direction: direction === 'up' ? t('Up') : t('Down'),
            unit: unit.display_name,
            field: field === 'volume' ? t('Volume') : t('Price'),
            hour: hourIndex,
          })}
          value={displayValue}
          disabled={isRowDisabled}
          className="w-full border-0 bg-transparent px-2 py-2 text-right text-sm focus:outline-none disabled:text-gray-500"
          onChange={isRowDisabled ? undefined : (event) => onValueChange(cellKey, event.target.value)}
          onFocus={isRowDisabled ? undefined : () => onCellFocus(cellKey)}
          onBlur={onCellBlur}
          onKeyDown={isRowDisabled ? undefined : (ev) => onCellKeyDown(cellKey, ev)}
          onMouseDown={
            isRowDisabled
              ? undefined
              : (ev) => {
                  onCellMouseDown(cellKey, ev)
                  ev.stopPropagation()
                }
          }
          onMouseOver={isRowDisabled ? undefined : () => onCellMouseOver(cellKey)}
          onDoubleClick={(ev) => {
            if (!isRowDisabled) {
              ev.currentTarget.select()
            }
          }}
        />
      </td>
    )
  }

  const upTotals = getDirectionTotals(rowValues, hourIndex, 'up', units, market)
  const downTotals = getDirectionTotals(rowValues, hourIndex, 'down', units, market)

  function renderMarketResultCell(
    direction: CmDirection,
    unit: MfrrBidTableRowProps['units'][number],
    borderRight: string
  ): ReactNode {
    const unitKey = getEnabledMfrrProductionUnitKey(unit)
    const marketVolume = getMarketResultVolume(unitKey, direction, hourIndex)

    return (
      <td
        key={`mr::${direction}::${unitKey}`}
        className={[
          'px-2 py-2 text-right text-sm text-gray-500',
          isMarketResultViolationRow ? 'bg-yellow-100' : 'bg-gray-100',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{
          borderRight,
          borderBottom: isLatestDisabledRow ? MFRR_TABLE_CLOSED_ROW_BORDER : MFRR_TABLE_CELL_BORDER,
        }}
        aria-disabled="true"
      >
        {marketVolume === undefined ? '-' : formatCmReadOnlyValue(marketVolume)}
      </td>
    )
  }

  return (
    <>
      {CMBID_DIRECTIONS.flatMap((typedDirection, directionIndex) => {
        const isLastDirection = directionIndex === 1
        const hasTotals = units.length > 1
        const result: ReactNode[] = []

        units.forEach((unit, unitIndex) => {
          const unitKey = getEnabledMfrrProductionUnitKey(unit)
          const hasMarketResult = marketResultUnitKeys.has(unitKey)
          const isFinalColumn = isLastDirection && !hasTotals && unitIndex === units.length - 1
          const groupEndBorder = isFinalColumn ? 'none' : MFRR_TABLE_GROUP_BORDER

          result.push(
            renderEditableCell(typedDirection, unit, 'volume', MFRR_TABLE_CELL_BORDER),
            renderEditableCell(typedDirection, unit, 'price', hasMarketResult ? MFRR_TABLE_CELL_BORDER : groupEndBorder)
          )

          if (hasMarketResult) {
            result.push(renderMarketResultCell(typedDirection, unit, groupEndBorder))
          }
        })

        if (hasTotals) {
          const totalsBorder = isLastDirection ? 'none' : MFRR_TABLE_GROUP_BORDER
          const totalCellClassName = [
            'px-2 py-2 text-right text-sm text-gray-500',
            isMarketResultViolationRow ? 'bg-yellow-100' : 'bg-gray-100',
          ].join(' ')
          const totalCellStyle = {
            borderBottom: isLatestDisabledRow ? MFRR_TABLE_CLOSED_ROW_BORDER : MFRR_TABLE_CELL_BORDER,
          }

          result.push(
            <td
              key={`${typedDirection}-total-volume`}
              className={totalCellClassName}
              style={{ ...totalCellStyle, borderRight: MFRR_TABLE_CELL_BORDER }}
              aria-disabled="true"
            >
              {formatCmReadOnlyValue(typedDirection === 'up' ? upTotals.totalVolume : downTotals.totalVolume)}
            </td>,
            <td
              key={`${typedDirection}-total-price`}
              className={totalCellClassName}
              style={{ ...totalCellStyle, borderRight: totalsBorder }}
              aria-disabled="true"
            >
              {formatCmReadOnlyValue(typedDirection === 'up' ? upTotals.totalPrice : downTotals.totalPrice)}
            </td>
          )
        }

        return result
      })}
    </>
  )
})

export function MfrrBidTable({
  units,
  dateStartTime,
  market,
  onEligibilityChange,
  onPayloadChange,
  initialValues,
  publishedBids = EMPTY_MFRR_BIDS,
  clearEditableRowsSignal,
  isReadOnly = false,
  cmGateClosureTimeComponents,
  cmInputMarginClosureTime,
  cmLeadDays,
  eamGateClosureMinutesBefore,
  eamInputMarginClosureTime,
  eamCopyFromSignal,
  cmBidsForEamCopy = [],
  isCmBidsForEamCopyPending = false,
  onCopyFromFailure,
  marketResultVolumesByUnit,
  highlightedRowIndices,
}: MfrrBidTableProps): ReactNode {
  const { t } = useTranslation()
  const rowCount = market === 'eam' ? EAM_ROW_COUNT : CM_ROW_COUNT
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const valuesByHourRef = useRef<Record<string, string>[]>([])
  const [mfrrTradingDeadlineCounter, setMfrrTradingDeadlineCounter] = useState(() => Date.now())

  const [valuesByHour, setValuesByHour] = useState<Record<string, string>[]>(() =>
    Array.from({ length: rowCount }, () => ({}))
  )
  const [activeCellKey, setActiveCellKey] = useState<string | null>(null)
  const lastHandledClearSignalRef = useRef<number | undefined>(undefined)
  const lastHandledCopyFromSignalRef = useRef<number | undefined>(undefined)
  const lastNotifiedPendingCopySignalRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    valuesByHourRef.current = valuesByHour
  }, [valuesByHour])

  useEffect(() => {
    // Reseed editable rows from saved bids. The parent memoizes initialValues on the
    // delivery date and unit layout, so this also resets local edits when those change.
    setValuesByHour(createInitialRows(rowCount, initialValues))
  }, [initialValues, rowCount])

  useEffect(() => {
    let intervalTimeout: ReturnType<typeof setInterval> | undefined
    const millisecondsUntilNextMinute = 60_000 - (Date.now() % 60_000)
    const timeout = setTimeout(() => {
      setMfrrTradingDeadlineCounter(Date.now())
      intervalTimeout = setInterval(() => {
        setMfrrTradingDeadlineCounter(Date.now())
      }, 60_000)
    }, millisecondsUntilNextMinute)

    return () => {
      clearTimeout(timeout)
      if (intervalTimeout) {
        clearInterval(intervalTimeout)
      }
    }
  }, [])

  const disabledHours = useMemo(() => {
    return new Set(
      Array.from({ length: rowCount }, (_unused, rowIndex) => {
        if (market === 'eam') {
          const rowStartTime = Datetime.addMinutes(dateStartTime, rowIndex * EAM_QUARTER_MINUTES)
          return hasEAMMarketClosedForDeliveryRow(
            rowStartTime,
            mfrrTradingDeadlineCounter,
            eamGateClosureMinutesBefore,
            eamInputMarginClosureTime
          )
            ? rowIndex
            : -1
        }
        const rowStartTime = Datetime.addHours(dateStartTime, rowIndex)
        return hasCMMarketClosedForDeliveryDate(
          rowStartTime,
          mfrrTradingDeadlineCounter,
          cmGateClosureTimeComponents,
          cmInputMarginClosureTime,
          cmLeadDays
        )
          ? rowIndex
          : -1
      }).filter((rowIndex) => rowIndex >= 0)
    )
  }, [
    cmGateClosureTimeComponents,
    cmLeadDays,
    cmInputMarginClosureTime,
    dateStartTime,
    eamGateClosureMinutesBefore,
    eamInputMarginClosureTime,
    market,
    mfrrTradingDeadlineCounter,
    rowCount,
  ])

  const latestDisabledEamRow = useMemo(() => {
    if (market !== 'eam' || disabledHours.size === 0 || disabledHours.size === rowCount) {
      return -1
    }

    return Math.max(...disabledHours)
  }, [disabledHours, market, rowCount])

  const adapter = useMemo(() => makeMfrrBidTableAdapter(units, rowCount), [units, rowCount])

  // Once a slot's deadline has passed it becomes historical: show only the latest published
  // bid for that slot (or 0/0 when none exists), regardless of any unpublished draft values.
  const publishedRowValues = useMemo(
    () => mapMfrrBidsToRowValues(publishedBids, dateStartTime, units, rowCount, market),
    [publishedBids, dateStartTime, units, rowCount, market]
  )

  const effectiveValuesByHour = useMemo(
    () =>
      valuesByHour.map((rowValues, hourIndex) =>
        disabledHours.has(hourIndex) ? (publishedRowValues[hourIndex] ?? {}) : rowValues
      ),
    [valuesByHour, disabledHours, publishedRowValues]
  )

  const {
    selectedCellKeys,
    selectedCellKeysRef,
    selectedCellKeysForClipboardRef,
    anchorCellKeyForClipboardRef,
    bulkReplaceOnNextInputRef,
    setAnchorCellKey,
    replaceSelection,
    clearSelection,
    handleCellMouseDown,
    handleCellMouseOver,
    getEditTargets,
    takeReplaceOnType,
  } = useTableCellSelection({ adapter, tableContainerRef })

  const clearValuesForSelection = useCallback((cellKeysToClear: Set<string>) => {
    if (cellKeysToClear.size === 0) {
      return
    }

    setValuesByHour((previous) => {
      const next = [...previous]

      cellKeysToClear.forEach((cellKey) => {
        const parsed = parseCmCellKey(cellKey)

        if (!parsed || parsed.hourIndex < 0 || parsed.hourIndex >= next.length) {
          return
        }

        next[parsed.hourIndex] = {
          ...(next[parsed.hourIndex] ?? {}),
          [cellKey]: '',
        }
      })

      return next
    })
  }, [])

  const { handleCellKeyDown: handleNavigationKeyDown } = useTableCellNavigation({
    adapter,
    clearValuesForSelection,
    clearSelection,
    replaceSelection,
    setAnchorCellKey,
    inputRefs,
    bulkReplaceOnNextInputRef,
    selectedCellKeysRef,
    isCellEditable: (cellKey) => {
      const coordinate = adapter.getCoordinate(cellKey)
      return coordinate !== null && !isReadOnly && !disabledHours.has(coordinate.rowIndex)
    },
  })

  const { handleCellValueChange, handleCellKeyDown } = useTableCellEdit({
    adapter,
    inputRefs,
    valuesByRow: valuesByHour,
    setValuesByRow: setValuesByHour,
    getCellRule: (cellKey) => getMfrrCellEditRule(market, parseCmCellKey(cellKey)?.field),
    getEditTargets,
    takeReplaceOnType,
    handleNavigationKeyDown,
  })

  useEffect(() => {
    if (clearEditableRowsSignal === undefined || clearEditableRowsSignal === lastHandledClearSignalRef.current) {
      return
    }

    lastHandledClearSignalRef.current = clearEditableRowsSignal
    clearSelection()

    setValuesByHour((previous) =>
      previous.map((rowValues, rowIndex) => {
        if (isReadOnly || disabledHours.has(rowIndex)) {
          return rowValues
        }

        return {}
      })
    )
  }, [clearEditableRowsSignal, clearSelection, disabledHours, isReadOnly])

  useEffect(() => {
    if (
      eamCopyFromSignal === undefined ||
      eamCopyFromSignal.id === lastHandledCopyFromSignalRef.current ||
      market !== 'eam'
    ) {
      return
    }

    if (isCmBidsForEamCopyPending) {
      if (lastNotifiedPendingCopySignalRef.current !== eamCopyFromSignal.id) {
        lastNotifiedPendingCopySignalRef.current = eamCopyFromSignal.id
        onCopyFromFailure?.('copy-source-loading')
      }
      return
    }

    lastNotifiedPendingCopySignalRef.current = undefined

    const cmBids = cmBidsForEamCopy.filter((bid) => {
      if (bid.market !== 'cm') {
        return false
      }

      if (eamCopyFromSignal.source === 'locked-cm-bids') {
        return bid.status === 'published'
      }

      return true
    })
    const emptyCopySourceReason = eamCopyFromSignal.source === 'cm-results' ? 'copy-no-cm-results' : 'copy-no-cm-bids'

    if (cmBids.length === 0) {
      lastHandledCopyFromSignalRef.current = eamCopyFromSignal.id
      onCopyFromFailure?.(emptyCopySourceReason)
      return
    }

    const configuredUnitIds = new Set(units.map((unit) => unit.id))
    const hasMatchingConfiguredUnit = cmBids.some(
      (bid) => bid.production_unit_id !== null && configuredUnitIds.has(bid.production_unit_id)
    )

    if (!hasMatchingConfiguredUnit) {
      lastHandledCopyFromSignalRef.current = eamCopyFromSignal.id
      onCopyFromFailure?.('copy-no-matching-unit-configured')
      return
    }

    const copiedVolumeRows = mapPublishedCmBidVolumesToEamRowValues(cmBids, dateStartTime, units)
    const hasCopiedRows = copiedVolumeRows.some((rowValues) => Object.keys(rowValues).length > 0)

    if (!hasCopiedRows) {
      lastHandledCopyFromSignalRef.current = eamCopyFromSignal.id
      onCopyFromFailure?.(emptyCopySourceReason)
      return
    }

    const hasEditableCopiedRows = copiedVolumeRows.some(
      (rowValues, rowIndex) => !isReadOnly && !disabledHours.has(rowIndex) && Object.keys(rowValues).length > 0
    )

    if (!hasEditableCopiedRows) {
      lastHandledCopyFromSignalRef.current = eamCopyFromSignal.id
      onCopyFromFailure?.('copy-target-rows-read-only')
      return
    }

    lastHandledCopyFromSignalRef.current = eamCopyFromSignal.id

    clearSelection()
    setValuesByHour((previous) =>
      previous.map((rowValues, rowIndex) => {
        if (isReadOnly || disabledHours.has(rowIndex)) {
          return rowValues
        }

        // The copy source is the full truth for volume; preserve hand-entered EAM prices.
        const copiedRowValues = copiedVolumeRows[rowIndex] ?? {}
        const preservedPriceValues = Object.fromEntries(
          Object.entries(rowValues).filter(([cellKey]) => parseCmCellKey(cellKey)?.field === 'price')
        )
        return { ...preservedPriceValues, ...copiedRowValues }
      })
    )
  }, [
    cmBidsForEamCopy,
    dateStartTime,
    disabledHours,
    eamCopyFromSignal,
    clearSelection,
    isCmBidsForEamCopyPending,
    isReadOnly,
    market,
    onCopyFromFailure,
    units,
  ])

  useTableCellClipboard({
    tableContainerRef,
    adapter,
    anchorCellKeyForClipboardRef,
    selectedCellKeysForClipboardRef,
    valuesByRowRef: valuesByHourRef,
    setValuesByRow: setValuesByHour,
    isCellEditable: (cellKey) => {
      const coordinate = adapter.getCoordinate(cellKey)
      return coordinate !== null && !isReadOnly && !disabledHours.has(coordinate.rowIndex)
    },
  })

  const handleCellBlur = useCallback(() => {
    setActiveCellKey(null)
  }, [])

  const registerInputRef = useCallback((cellKey: string, element: HTMLInputElement | null) => {
    if (element === null) {
      delete inputRefs.current[cellKey]
      return
    }

    inputRefs.current[cellKey] = element
  }, [])

  useEffect(() => {
    const areAllEntriesValid = effectiveValuesByHour.every((row) =>
      Object.entries(row).every(([cellKey, rawValue]) => {
        const parsedCellKey = parseCmCellKey(cellKey)
        return isValidMfrrBidValue(rawValue, parsedCellKey?.field, market)
      })
    )

    if (!areAllEntriesValid) {
      onEligibilityChange({
        canSubmit: false,
        disabledReason: t('Remove invalid values before submitting.'),
      })
      onPayloadChange([])
      return
    }

    const bids = mapRowValuesToMfrrBids(effectiveValuesByHour, dateStartTime, units, market)

    onEligibilityChange({ canSubmit: true, disabledReason: '' })
    onPayloadChange(bids)
  }, [dateStartTime, market, onEligibilityChange, onPayloadChange, t, units, effectiveValuesByHour])

  const marketResultUnitKeys = useMemo(
    () => new Set(marketResultVolumesByUnit?.keys() ?? []),
    [marketResultVolumesByUnit]
  )

  const getMarketResultVolume = useCallback(
    (unitKey: string, direction: CmDirection, rowIndex: number): number | undefined => {
      const directional = marketResultVolumesByUnit?.get(unitKey)

      if (!directional) {
        return undefined
      }

      return directional[direction].get(getMarketResultHourForRow(market, rowIndex))
    },
    [marketResultVolumesByUnit, market]
  )

  const valueFieldsPerDirection = useMemo(
    () => getMfrrValueFieldsPerDirection(units, marketResultUnitKeys),
    [units, marketResultUnitKeys]
  )

  const directionHeaderSpan = valueFieldsPerDirection.length
  const valueColumnCount = directionHeaderSpan * 2

  const groupEndFieldIndices = useMemo(() => {
    const groupEnds = new Set<number>()
    let fieldIndex = 0

    for (const unit of units) {
      fieldIndex += marketResultUnitKeys.has(getEnabledMfrrProductionUnitKey(unit)) ? 3 : 2
      groupEnds.add(fieldIndex - 1)
    }

    if (units.length > 1) {
      groupEnds.add(fieldIndex + 1)
    }

    return groupEnds
  }, [units, marketResultUnitKeys])

  return (
    <div className="w-full max-w-full min-w-0 grow">
      <div
        className="w-full max-w-full overflow-x-auto overflow-y-auto rounded border border-gray-300"
        ref={tableContainerRef}
      >
        <table className="w-max min-w-full table-fixed border-separate border-spacing-0 text-sm">
          <colgroup>
            <col className="w-[5rem] min-w-[5rem] max-w-[5rem]" />
            {Array.from({ length: valueColumnCount }, (_unused, index) => (
              <col key={`value-col-${index}`} className="w-[4.5rem]" />
            ))}
          </colgroup>
          <thead className="sticky top-0 z-30 bg-gray-100">
            <tr>
              <th
                rowSpan={3}
                className="sticky left-0 top-0 z-40 bg-gray-100 px-2 py-2 text-left w-[5rem] min-w-[5rem] max-w-[5rem] font-semibold"
                style={{
                  color: colors.black,
                  borderRight: MFRR_TABLE_GROUP_BORDER,
                  borderBottom: MFRR_TABLE_CELL_BORDER,
                }}
              >
                {t('Hour')}
              </th>
              {CMBID_DIRECTIONS.map((direction, directionIndex) => {
                const isUp = direction === 'up'

                return (
                  <th
                    key={direction}
                    colSpan={directionHeaderSpan}
                    className="px-2 py-2 text-center font-semibold"
                    style={{
                      backgroundColor: getMfrrDirectionBackground(direction),
                      color: colors.black,
                      borderRight: directionIndex === 1 ? 'none' : MFRR_TABLE_GROUP_BORDER,
                      borderBottom: getMfrrDirectionInnerBorder(direction),
                    }}
                  >
                    {market === 'cm'
                      ? isUp
                        ? t('mFRR-CM Up')
                        : t('mFRR-CM Down')
                      : isUp
                        ? t('mFRR-EAM Up')
                        : t('mFRR-EAM Down')}
                  </th>
                )
              })}
            </tr>
            <tr>
              {CMBID_DIRECTIONS.flatMap((direction, directionIndex) => {
                const hasTotals = units.length > 1
                const backgroundColor = getMfrrDirectionBackground(direction)
                const borderBottom = getMfrrDirectionInnerBorder(direction)

                const result = units.map((unit, unitIndex) => {
                  const unitKey = getEnabledMfrrProductionUnitKey(unit)
                  const isFinalColumn = directionIndex === 1 && !hasTotals && unitIndex === units.length - 1

                  return (
                    <th
                      key={`${direction}-${unit.id}`}
                      colSpan={marketResultUnitKeys.has(unitKey) ? 3 : 2}
                      className="px-2 py-1 text-center font-semibold"
                      style={{
                        backgroundColor,
                        color: colors.black,
                        borderRight: isFinalColumn ? 'none' : MFRR_TABLE_GROUP_BORDER,
                        borderBottom,
                      }}
                    >
                      {unit.display_name}
                    </th>
                  )
                })

                if (hasTotals) {
                  result.push(
                    <th
                      key={`${direction}-total`}
                      colSpan={2}
                      className="px-2 py-1 text-center font-semibold"
                      style={{
                        backgroundColor,
                        color: colors.black,
                        borderRight: directionIndex === 1 ? 'none' : MFRR_TABLE_GROUP_BORDER,
                        borderBottom,
                      }}
                    >
                      {t('Total')}
                    </th>
                  )
                }

                return result
              })}
            </tr>
            <tr>
              {CMBID_DIRECTIONS.flatMap((direction, directionIndex) => {
                const backgroundColor = getMfrrDirectionBackground(direction)
                const innerBorder = getMfrrDirectionInnerBorder(direction)

                return valueFieldsPerDirection.map((field, fieldIndex) => {
                  const isFinalColumn = directionIndex === 1 && fieldIndex === valueFieldsPerDirection.length - 1

                  return (
                    <th
                      key={`${direction}-${fieldIndex}`}
                      className="px-2 py-1 text-center text-xs font-medium font-semibold"
                      style={{
                        backgroundColor,
                        color: colors.black,
                        borderRight: isFinalColumn
                          ? 'none'
                          : groupEndFieldIndices.has(fieldIndex)
                            ? MFRR_TABLE_GROUP_BORDER
                            : innerBorder,
                        borderBottom: MFRR_TABLE_CELL_BORDER,
                      }}
                    >
                      {field === 'volume'
                        ? t('Volume (MW)')
                        : field === 'price'
                          ? t('Price (SEK)')
                          : t('CM Volume Result (MW)')}
                    </th>
                  )
                })
              })}
            </tr>
          </thead>
          <tbody>
            {valuesByHour.map((_rowValues, hourIndex) => {
              const isClosedByDeadline = disabledHours.has(hourIndex)
              const displayRowValues = effectiveValuesByHour[hourIndex] ?? {}

              return (
                <tr key={getMfrrRowStartTime(dateStartTime, hourIndex, market)} className="bg-white">
                  <td
                    className={`sticky left-0 z-10 ${highlightedRowIndices?.has(hourIndex) ? 'bg-yellow-100' : 'bg-white'} px-2 py-2 text-left font-medium whitespace-nowrap w-[5rem] min-w-[5rem] max-w-[5rem]`}
                    style={{
                      borderRight: MFRR_TABLE_GROUP_BORDER,
                      borderBottom:
                        hourIndex === latestDisabledEamRow ? MFRR_TABLE_CLOSED_ROW_BORDER : MFRR_TABLE_CELL_BORDER,
                    }}
                  >
                    {getMfrrRowLabel(dateStartTime, hourIndex, market)}
                  </td>
                  <MfrrBidTableRow
                    hourIndex={hourIndex}
                    market={market}
                    isRowDisabled={isReadOnly || isClosedByDeadline}
                    isLatestDisabledRow={hourIndex === latestDisabledEamRow}
                    isMarketResultViolationRow={highlightedRowIndices?.has(hourIndex) ?? false}
                    units={units}
                    rowValues={displayRowValues}
                    activeCellKey={activeCellKey}
                    selectedCellKeys={selectedCellKeys}
                    marketResultUnitKeys={marketResultUnitKeys}
                    getMarketResultVolume={getMarketResultVolume}
                    onValueChange={handleCellValueChange}
                    onCellFocus={setActiveCellKey}
                    onCellBlur={handleCellBlur}
                    onCellKeyDown={handleCellKeyDown}
                    onCellMouseDown={handleCellMouseDown}
                    onCellMouseOver={handleCellMouseOver}
                    registerInputRef={registerInputRef}
                  />
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
